import { UseMutationResult } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { useRef } from 'react';
import { ErrorResponse } from '../definition';

// Global debounced functions map
const debouncedMutations = new Map<string, (...args: any[]) => void>();

export function useDebouncedMutation<
  Req = unknown,
  Res = never,
  Variables = Req,
  Key = any
>(
  mutation: UseMutationResult<Res, ErrorResponse, Variables, Key>,
  groupKey: string, // Group-based debounce key (the instances with the same key will debounce together)
  delay = 500
) {
  const mutateFnRef = useRef<(data: Variables) => Promise<Res>>();

  // Store reference to actual mutation function
  mutateFnRef.current = mutation.mutateAsync;

  // Create a debounced function per groupKey if it doesn't exist
  if (!debouncedMutations.has(groupKey)) {
    debouncedMutations.set(
      groupKey,
      debounce((data: Variables) => {
        mutateFnRef.current?.(data);
      }, delay)
    );
  }

  const debouncedMutate = (data: Variables) => {
    return debouncedMutations.get(groupKey)!(data);
  };

  return { ...mutation, debouncedMutate };
}
